import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardService } from '../../../services/dashboard.service';
import { getWidget, WidgetDef } from '../registry';

interface Params {
    institutionId: string;
    boardKey: string;
    fallbackIds: string[];
    editable: boolean;
    /** Called with the server error when a save or reset is refused (e.g. 4xx on a locked widget). */
    onError?: (error: unknown) => void;
}

export default function useDashboardLayout({ institutionId, boardKey, fallbackIds, editable, onError }: Params) {
    const [order, setOrder] = useState<string[]>([]);
    const [locked, setLocked] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    // Locked widgets set by an admin or head must survive in `order` even when this client does not
    // know the id (an older bundle): they are not rendered (widgets filters on the registry) but they
    // are saved back, so the backend never sees a locked id dropped.
    const lockedRef = useRef<string[]>([]);
    lockedRef.current = locked;
    const clean = useCallback(
        (ids: string[], lock: string[] = lockedRef.current) => ids.filter(id => getWidget(id) !== undefined || lock.includes(id)),
        [],
    );
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const applyServer = useCallback((l: { widgetIds?: string[]; lockedWidgetIds?: string[] }) => {
        const lock = l.lockedWidgetIds ?? [];
        setLocked(lock);
        lockedRef.current = lock;
        const saved = clean(l.widgetIds ?? [], lock);
        const base = saved.length > 0 ? saved : clean(fallbackRef.current, lock);
        setOrder([...base, ...lock.filter(id => !base.includes(id))]);
    }, [clean]);

    // Read the latest fallback from effects/callbacks WITHOUT making the array's identity a
    // dependency: callers may pass a fresh literal on some renders. A derived primitive
    // `fallbackKey` drives effect re-runs (so a role-preview switch still refreshes the shown
    // preset) while the ref hands the effect the current array.
    const fallbackRef = useRef(fallbackIds);
    fallbackRef.current = fallbackIds;
    const fallbackKey = fallbackIds.join('|');

    useEffect(() => {
        let live = true;
        setLoading(true);
        if (!editable) {
            // Role preview / read-only: show the fallback, never touch storage.
            setOrder(clean(fallbackRef.current));
            setLoading(false);
            return () => { live = false; };
        }
        DashboardService.getLayout(institutionId, boardKey)
            .then(l => { if (live) applyServer(l); })
            .catch(() => { if (live) { setLocked([]); setOrder(clean(fallbackRef.current, [])); } })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [institutionId, boardKey, editable, clean, applyServer, fallbackKey]);

    const persist = useCallback((next: string[]) => {
        if (!editable) return;
        // Any locked id missing from the list is appended: a save must never drop a locked widget.
        const missing = lockedRef.current.filter(id => !next.includes(id));
        DashboardService.saveLayout(institutionId, boardKey, { widgetIds: missing.length > 0 ? [...next, ...missing] : next }).catch(err => {
            onErrorRef.current?.(err);
            // The refused change is not on the server: show what the server really holds.
            DashboardService.getLayout(institutionId, boardKey).then(applyServer).catch(() => {});
        });
    }, [institutionId, boardKey, editable, applyServer]);

    const add = useCallback((id: string) => {
        if (!editable) return;
        setOrder(prev => {
            if (prev.includes(id) || !getWidget(id)) return prev;
            const next = [...prev, id];
            persist(next);
            return next;
        });
    }, [editable, persist]);

    const remove = useCallback((id: string) => {
        if (!editable) return;
        if (lockedRef.current.includes(id)) return; // locked by an admin or head: no remove
        setOrder(prev => {
            const next = prev.filter(x => x !== id);
            persist(next);
            return next;
        });
    }, [editable, persist]);

    const reorder = useCallback((from: number, to: number) => {
        if (!editable) return;
        setOrder(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            persist(next);
            return next;
        });
    }, [editable, persist]);

    // "Reset" asks the backend, which returns the board to what an admin or head set (else the role
    // default, else empty = the built-in preset). It must never PUT the preset as the person's own
    // layout: that would pin them off every later change to the default.
    const reset = useCallback(() => {
        if (!editable) return;
        DashboardService.resetMyBoard(institutionId, boardKey)
            .then(applyServer)
            .catch(err => onErrorRef.current?.(err));
    }, [editable, institutionId, boardKey, applyServer]);

    const widgets = useMemo<WidgetDef[]>(
        () => order.map(getWidget).filter((w): w is WidgetDef => w !== undefined),
        [order],
    );

    return { order, widgets, locked, add, remove, reorder, reset, loading };
}
